import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { LayoutDashboard, Users, Building2, Ticket, CreditCard, Wrench, Settings, Inbox, Coins, BookOpen } from "lucide-react";

const ITEMS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Vue d'ensemble" },
  { to: "/admin/users", icon: Users, label: "Visiteurs" },
  { to: "/admin/commerces-manage", icon: Building2, label: "Établissements" },
  { to: "/admin/queues", icon: Ticket, label: "Files & Tickets" },
  { to: "/admin/payments", icon: CreditCard, label: "Paiements" },
  { to: "/admin/monetization", icon: Coins, label: "Monétisation" },
  { to: "/admin/support", icon: Inbox, label: "Support" },
  { to: "/admin/maintenance", icon: Settings, label: "Maintenance" },
  { to: "/admin/guides", icon: BookOpen, label: "Guides PDF" },
];

export default function AdminLayout() {
  const { loading, role, user } = useAuth();
  if (loading) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;
  if (role !== "client" || !user?.isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="container mx-auto px-4 md:px-8 py-8 max-w-7xl" data-testid="admin-console">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="overline mb-1">Administration</p>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-ink">Console administrateur</h1>
          <p className="mt-1 text-subtle text-sm">Connecté en tant que <strong className="text-ink">{user?.email}</strong></p>
        </div>
        <Link to="/" className="text-sm text-subtle hover:text-ink">← Retour au site</Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 md:gap-6">
        {/* MOBILE : barre horizontale scrollable d'onglets (compacte, 1 ligne) */}
        <aside className="md:hidden border-y border-border bg-surface/30 -mx-4 px-2" data-testid="admin-nav-mobile">
          <nav className="flex overflow-x-auto gap-1 py-2 scrollbar-thin">
            {ITEMS.map(({ to, end, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `shrink-0 flex items-center gap-1.5 px-3 h-9 text-xs font-medium border whitespace-nowrap rounded-md ${
                    isActive ? "bg-ink text-white border-ink" : "text-ink bg-white border-border hover:border-ink"
                  }`
                }
                data-testid={`admin-nav-mobile-${to.replace(/\//g, "-")}`}
              >
                <Icon size={14} /> <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        {/* DESKTOP : sidebar verticale classique */}
        <aside className="hidden md:block border border-border bg-surface/30 h-fit md:sticky md:top-20" data-testid="admin-nav">
          <nav className="flex flex-col">
            {ITEMS.map(({ to, end, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 h-12 text-sm font-medium border-b border-border last:border-b-0 ${
                    isActive ? "bg-ink text-white" : "text-ink hover:bg-white"
                  }`
                }
                data-testid={`admin-nav-${to.replace(/\//g, "-")}`}
              >
                <Icon size={16} /> <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
